import * as Pages from "@/components/Pages";
import * as Paper from "@/components/PaperContainer";
import * as React from "react";

import { SpacePageNavigation } from "@/components/SpacePageNavigation";
import { Modal, PrimaryButton, SecondaryButton, showErrorToast, showSuccessToast } from "turboui";
import {
  CrmContact,
  CrmStatus,
  CRM_STATUSES,
  crmToolLabel,
  getCrmContacts,
  useCreateCrmContact,
  useUpdateCrmContact,
} from "@/models/crmContacts";
import { useReloadContactsSignal } from "@/signals";

import { useLoadedData } from "./loader";

// Direction B — "Structured Pills": status reads as both color and shape so the
// table is scannable pre-attentively, in funnel order (lead → … → lost).
const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  lead: {
    label: "Lead",
    className: "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",
  },
  contacted: {
    label: "Contacted",
    className: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900",
  },
  qualified: {
    label: "Qualified",
    className:
      "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900",
  },
  customer: {
    label: "Customer",
    className:
      "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900",
  },
  lost: {
    label: "Lost",
    className: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900",
  },
};

function StatusPill({ status }: { status?: string | null }) {
  const cfg = STATUS_STYLES[status ?? "lead"] ?? STATUS_STYLES["lead"]!;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

export function Page() {
  const { space, contacts: initialContacts } = useLoadedData();
  const [contacts, setContacts] = React.useState<CrmContact[]>(initialContacts);
  const [query, setQuery] = React.useState("");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<CrmContact | null>(null);

  const canEdit = !!space.permissions?.canEdit;
  const label = crmToolLabel(space.name);

  // Live updates: refetch the book whenever a contact is saved anywhere in the
  // company (e.g. an agent via the Highway, or a teammate in another tab).
  const reload = React.useCallback(async () => {
    try {
      const res = await getCrmContacts({ spaceId: space.id! });
      setContacts(res.contacts ?? []);
    } catch (e) {
      console.error("Failed to reload contacts", e);
    }
  }, [space.id]);
  useReloadContactsSignal(reload);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) =>
      [c.name, c.company, c.email, c.status, c.source]
        .filter((v): v is string => Boolean(v))
        .some((v) => v.toLowerCase().includes(q)),
    );
  }, [contacts, query]);

  const openNew = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (c: CrmContact) => {
    setEditing(c);
    setModalOpen(true);
  };

  const handleSaved = (saved: CrmContact) => {
    setContacts((prev) => {
      const exists = prev.some((c) => c.id === saved.id);
      return exists ? prev.map((c) => (c.id === saved.id ? saved : c)) : [...prev, saved];
    });
    setModalOpen(false);
  };

  return (
    <Pages.Page title={[label, space.name!]} testId="crm-page">
      <Paper.Root size="large">
        <SpacePageNavigation space={space} />

        <Paper.Body minHeight="500px">
          <Paper.Header
            title="Contacts"
            layout="title-center-actions-left"
            actions={
              canEdit ? (
                <PrimaryButton size="sm" onClick={openNew} testId="new-contact">
                  New contact
                </PrimaryButton>
              ) : null
            }
            underline
          />

          <div className="flex items-center justify-between gap-3 mt-4 mb-3 px-1">
            <div className="text-sm text-content-dimmed" data-test-id="crm-count">
              {contacts.length} {contacts.length === 1 ? "contact" : "contacts"}
            </div>
            {contacts.length > 0 && (
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search contacts"
                className="text-sm border border-surface-outline rounded-md px-3 py-1.5 bg-surface-base w-56 focus:outline-none focus:ring-2 focus:ring-brand-1"
                data-test-id="crm-search"
              />
            )}
          </div>

          {contacts.length < 1 ? (
            <ZeroState canEdit={canEdit} onAdd={openNew} label={label} />
          ) : (
            <ContactsTable contacts={filtered} canEdit={canEdit} onEdit={openEdit} />
          )}
        </Paper.Body>
      </Paper.Root>

      {modalOpen && (
        <ContactModal
          spaceId={space.id!}
          contact={editing}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </Pages.Page>
  );
}

function ContactsTable({
  contacts,
  canEdit,
  onEdit,
}: {
  contacts: CrmContact[];
  canEdit: boolean;
  onEdit: (c: CrmContact) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-surface-outline">
        <thead>
          <tr className="text-left text-xs font-semibold uppercase tracking-wide text-content-dimmed bg-surface-dimmed">
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Company</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Source</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-outline">
          {contacts.map((c) => (
            <tr
              key={c.id}
              className={"hover:bg-surface-highlight transition-colors " + (canEdit ? "cursor-pointer" : "")}
              onClick={canEdit ? () => onEdit(c) : undefined}
              data-test-id={`crm-row-${c.id}`}
            >
              <td className="px-3 py-2.5">
                <div className="font-medium text-content-accent">{c.name}</div>
                {c.email ? <div className="text-xs text-content-dimmed">{c.email}</div> : null}
              </td>
              <td className="px-3 py-2.5 text-sm text-content-base">{c.company || "—"}</td>
              <td className="px-3 py-2.5">
                <StatusPill status={c.status} />
              </td>
              <td className="px-3 py-2.5 text-sm text-content-dimmed">{c.source || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {contacts.length < 1 && (
        <div className="text-center text-sm text-content-dimmed py-8">No contacts match your search.</div>
      )}
    </div>
  );
}

function ZeroState({ canEdit, onAdd, label }: { canEdit: boolean; onAdd: () => void; label: string }) {
  return (
    <div className="text-center text-base text-content-dimmed mt-20">
      <div>
        {label === "CRM"
          ? "Track leads, prospects, and customers. One shared book across the whole company."
          : "Save and look up contacts. One shared book across the whole company."}
      </div>
      {canEdit && (
        <div className="mt-4">
          <PrimaryButton size="sm" onClick={onAdd} testId="new-contact-empty">
            Add your first contact
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}

function ContactModal({
  spaceId,
  contact,
  onClose,
  onSaved,
}: {
  spaceId: string;
  contact: CrmContact | null;
  onClose: () => void;
  onSaved: (c: CrmContact) => void;
}) {
  const [createContact] = useCreateCrmContact();
  const [updateContact] = useUpdateCrmContact();

  const [name, setName] = React.useState(contact?.name ?? "");
  const [email, setEmail] = React.useState(contact?.email ?? "");
  const [company, setCompany] = React.useState(contact?.company ?? "");
  const [status, setStatus] = React.useState<CrmStatus>((contact?.status as CrmStatus) ?? "lead");
  const [source, setSource] = React.useState(contact?.source ?? "");
  const [notes, setNotes] = React.useState(contact?.notes ?? "");
  const [saving, setSaving] = React.useState(false);

  const isEdit = Boolean(contact);

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);

    try {
      const payload = {
        name: name.trim(),
        email: email || null,
        company: company || null,
        status,
        source: source || null,
        notes: notes || null,
      };

      const res =
        isEdit && contact
          ? await updateContact({ id: contact.id, ...payload })
          : await createContact({ spaceId, ...payload });

      onSaved(res.contact);
      showSuccessToast("Saved", isEdit ? "Contact updated" : "Contact added");
    } catch (e) {
      console.error("Failed to save contact", e);
      showErrorToast("Error", "Failed to save contact");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={isEdit ? "Edit contact" : "New contact"} size="medium">
      <div className="space-y-4">
        <Field label="Name">
          <TextInput value={name} onChange={setName} placeholder="Full name" autoFocus testId="contact-name" />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Email">
            <TextInput value={email} onChange={setEmail} placeholder="name@company.com" testId="contact-email" />
          </Field>
          <Field label="Company">
            <TextInput value={company} onChange={setCompany} placeholder="Company" testId="contact-company" />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Status">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as CrmStatus)}
              data-test-id="contact-status"
              className="w-full text-sm border border-surface-outline rounded-md px-3 py-2 bg-surface-base focus:outline-none focus:ring-2 focus:ring-brand-1"
            >
              {CRM_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_STYLES[s]!.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Source">
            <TextInput value={source} onChange={setSource} placeholder="Referral, Inbound…" testId="contact-source" />
          </Field>
        </div>

        <Field label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes"
            rows={3}
            className="w-full text-sm border border-surface-outline rounded-md px-3 py-2 bg-surface-base focus:outline-none focus:ring-2 focus:ring-brand-1"
            data-test-id="contact-notes"
          />
        </Field>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <SecondaryButton size="sm" onClick={onClose}>
          Cancel
        </SecondaryButton>
        <PrimaryButton size="sm" onClick={submit} loading={saving} disabled={!name.trim()} testId="save-contact">
          {isEdit ? "Save changes" : "Add contact"}
        </PrimaryButton>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-content-base mb-1">{label}</label>
      {children}
    </div>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  autoFocus,
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  testId?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      data-test-id={testId}
      className="w-full text-sm border border-surface-outline rounded-md px-3 py-2 bg-surface-base focus:outline-none focus:ring-2 focus:ring-brand-1"
    />
  );
}
